import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthToken } from '@/features/auth';
import { Button } from '@/shared/ui/Button';
import { AuthLayout } from './ui/AuthLayout';
import { authMessageClassName } from './ui/authForm';

// Авторизация по временному токену — impersonation из админки: менеджер
// жмёт «Авторизоваться», бек отдаёт URL /partners/token?id=&token=, который
// открывается в новом окне. 1:1 с легаси TokenCtrl: id+token из query →
// POST /user/authToken (useAuthToken ставит cookies + переводит store в
// authenticated, минуя 2FA) → авто-редирект в кабинет.
//
// Роут вне RedirectIfAuthed — impersonation должен срабатывать всегда и
// перетирать текущую сессию, если она была.

export function TokenPage() {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const id = Number.parseInt(params.get('id') ?? '', 10);
  const hasParams = Boolean(token) && Number.isInteger(id);

  const authTokenMutation = useAuthToken();
  const [state, setState] = useState<'authing' | 'not-activated' | 'error'>(
    hasParams ? 'authing' : 'error',
  );
  // Как в VerifyEmail: в состоянии причина, а не готовый текст — t() внутри
  // useState застыл бы на языке первого рендера.
  const [errorReason, setErrorReason] = useState<'missingParams' | 'failed' | 'network' | null>(
    hasParams ? null : 'missingParams',
  );
  const [serverMessage, setServerMessage] = useState<string | null>(null);

  const errorText =
    serverMessage ??
    (errorReason === 'missingParams'
      ? t('token.missingParams')
      : errorReason === 'network'
        ? t('common:networkError')
        : t('token.failed'));

  useEffect(() => {
    if (!hasParams) return;
    let cancelled = false;
    authTokenMutation
      .mutateAsync({ token, id })
      .then((outcome) => {
        if (cancelled) return;
        if (outcome.kind === 'ok') {
          // store уже authenticated — редирект в кабинет (index → /stat).
          navigate('/', { replace: true });
        } else if (outcome.kind === 'not-activated') {
          setState('not-activated');
        } else {
          setState('error');
          setErrorReason('failed');
          setServerMessage(outcome.message ?? null);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setState('error');
        setErrorReason('network');
      });
    return () => {
      cancelled = true;
    };
    // mutation стабилен; token+id берутся один раз при mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id, hasParams]);

  // Заголовок и вопрос со ссылкой — по состоянию; раскладка одна (AuthLayout).
  const header = {
    authing: { title: t('login.title'), questionText: '' },
    'not-activated': { title: t('token.notActivatedTitle'), questionText: '' },
    error: { title: t('token.failedTitle'), questionText: t('rememberedPassword') },
  }[state];

  return (
    <AuthLayout
      title={header.title}
      questionText={header.questionText}
      linkTo="/login"
      linkText={t('signIn')}
    >
      {state === 'authing' && (
        <>
          <p className={authMessageClassName()}>{t('token.pending')}</p>
        </>
      )}

      {state === 'not-activated' && (
        <>
          <p className={authMessageClassName()}>
            {t('token.notActivatedText')}
          </p>
        </>
      )}

      {state === 'error' && (
        <>
          <p className={authMessageClassName('error')}>{errorText}</p>
          <div className="mt-8 w-full">
            <Button
              type="button"
              className="min-h-[48px] w-full !px-0"
              onClick={() => navigate('/login', { replace: true })}
            >
              {t('back')}
            </Button>
          </div>
        </>
      )}
    </AuthLayout>
  );
}
